import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/api';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import { ThemeContext } from '../context/ThemeContext';
import { CURRENCY } from '../config/currency';

interface InvoiceItem {
  id: number;
  menuItemName: string;
  quantity: number;
  price: number;
  comment?: string;
  portionName?: string;
  toppings?: { id: number; name: string }[];
}

interface VenueTable {
  id: number;
  name: string;
  displayNumber?: string;
  floor?: { name: string };
  section?: { name: string };
}

interface Invoice {
  id: number;
  order?: {
    id: number;
    customOrderId?: string;
    items: InvoiceItem[];
    status?: string;
    orderType?: string;
    guestName?: string;
    guestPhone?: string;
    guestEmail?: string;
    customerName?: string;
    customerPhone?: string;
    customerEmail?: string;
    isGuestOrder?: boolean;
    isStaffOrder?: boolean;
    orderNotes?: string;
    user?: {
      id: number;
      username: string;
      email?: string;
      phoneNumber?: string;
      name?: string;
    };
    vendor?: {
      id: number;
      username: string;
      email?: string;
      phoneNumber?: string;
      name?: string;
    };
    venueTable?: VenueTable;
    qrCode?: {
      qrString: string;
      venueTable?: VenueTable;
    };
  };
  createdAt: string;
  total: number;
  // Tax breakdown fields
  subtotal?: number;
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  serviceChargeRate?: number;
  serviceChargeAmount?: number;
  discountAmount?: number;
  taxType?: string;
}

const InvoiceDetail: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useContext(ThemeContext);

  useEffect(() => {
    if (!invoiceId) {
      setError("Invoice ID is missing.");
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get<Invoice>(`/invoices/${invoiceId}`)
      .then(res => {
        setInvoice(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.message || err.message || "Failed to fetch invoice details.");
        setLoading(false);
      });
  }, [invoiceId]);

  const renderInvoiceContent = () => {
    if (loading) {
      return <p className="text-center py-8" style={{ color: theme.gray[500] }}>Loading invoice details...</p>;
    }

    if (error) {
      return <p className="p-4 bg-red-100 dark:bg-red-800 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded-md text-center">{error}</p>;
    }

    if (!invoice) {
      return <p className="text-center py-8" style={{ color: theme.gray[500] }}>No invoice data found.</p>;
    }

    const order = invoice.order;
    const itemsToDisplay = order?.items || [];
    const vendor = order?.vendor;
    const table = order?.venueTable || order?.qrCode?.venueTable;

    // Determine customer info
    let customerName = 'N/A';
    let customerPhone = '';
    let customerEmail = '';
    let customerType = 'Walk-in';

    if (order?.isStaffOrder) {
      customerName = order.customerName || 'Walk-in Customer';
      customerPhone = order.customerPhone || '';
      customerEmail = order.customerEmail || '';
      customerType = 'Walk-in/Counter';
    } else if (order?.isGuestOrder) {
      customerName = order.guestName || 'Guest';
      customerPhone = order.guestPhone || '';
      customerEmail = order.guestEmail || '';
      customerType = 'Guest';
    } else if (order?.user) {
      customerName = order.user.name || order.user.username;
      customerPhone = order.user.phoneNumber || '';
      customerEmail = order.user.email || '';
      customerType = 'Registered';
    }

    // Calculate subtotal (before tax/charges)
    const subtotal = itemsToDisplay.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    return (
      <div className="max-w-4xl mx-auto">
        <div className="shadow-xl rounded-2xl p-6 sm:p-4 md:p-6 lg:p-8" style={{ backgroundColor: theme.background }}>

          {/* Restaurant Header */}
          {vendor && (
            <div className="text-center pb-6 border-b mb-6" style={{ borderColor: theme.gray[200] }}>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2" style={{ color: theme.primary }}>
                {vendor.name || vendor.username}
              </h1>
              {vendor.phoneNumber && (
                <p className="text-sm" style={{ color: theme.gray[600] }}>Phone: {vendor.phoneNumber}</p>
              )}
              {vendor.email && (
                <p className="text-sm" style={{ color: theme.gray[600] }}>Email: {vendor.email}</p>
              )}
            </div>
          )}

          {/* Invoice Header */}
          <div className="pb-6 border-b mb-6" style={{ borderColor: theme.gray[200] }}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: theme.primary }}>
                  Invoice #{invoice.id}
                </h2>
                <p className="text-sm" style={{ color: theme.gray[600] }}>
                  Order ID: {order?.customOrderId || order?.id || 'N/A'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold" style={{ color: theme.gray[700] }}>Invoice Date</p>
                <p className="text-sm" style={{ color: theme.gray[600] }}>
                  {new Date(invoice.createdAt).toLocaleDateString()}
                </p>
                <p className="text-xs" style={{ color: theme.gray[500] }}>
                  {new Date(invoice.createdAt).toLocaleTimeString()}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* Customer Information */}
              <div>
                <h3 className="text-sm font-bold mb-2" style={{ color: theme.gray[700] }}>CUSTOMER DETAILS</h3>
                <div className="space-y-1 text-sm" style={{ color: theme.gray[600] }}>
                  <p><span className="font-semibold">Name:</span> {customerName}</p>
                  <p><span className="font-semibold">Type:</span> {customerType}</p>
                  {customerPhone && <p><span className="font-semibold">Phone:</span> {customerPhone}</p>}
                  {customerEmail && <p><span className="font-semibold">Email:</span> {customerEmail}</p>}
                </div>
              </div>

              {/* Order Information */}
              <div>
                <h3 className="text-sm font-bold mb-2" style={{ color: theme.gray[700] }}>ORDER DETAILS</h3>
                <div className="space-y-1 text-sm" style={{ color: theme.gray[600] }}>
                  {order?.orderType && (
                    <p><span className="font-semibold">Type:</span> {order.orderType}</p>
                  )}
                  {order?.status && (
                    <p><span className="font-semibold">Status:</span> {order.status}</p>
                  )}
                  {table && (
                    <>
                      <p><span className="font-semibold">Table:</span> {table.displayNumber || table.name}</p>
                      {table.floor && <p><span className="font-semibold">Floor:</span> {table.floor.name}</p>}
                      {table.section && <p><span className="font-semibold">Section:</span> {table.section.name}</p>}
                    </>
                  )}
                </div>
              </div>
            </div>

            {order?.orderNotes && (
              <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: theme.gray[50] }}>
                <p className="text-xs font-semibold mb-1" style={{ color: theme.gray[700] }}>Order Notes:</p>
                <p className="text-sm italic" style={{ color: theme.gray[600] }}>{order.orderNotes}</p>
              </div>
            )}
          </div>

          {/* Items Section */}
          <section className="mb-6">
            <h3 className="text-lg font-bold mb-4" style={{ color: theme.gray[700] }}>ITEMS</h3>
            {itemsToDisplay.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: theme.gray[300] }}>
                      <th className="text-left py-3 px-2 text-sm font-bold" style={{ color: theme.gray[700], whiteSpace: 'nowrap' }}>Item</th>
                      <th className="text-center py-3 px-2 text-sm font-bold" style={{ color: theme.gray[700], whiteSpace: 'nowrap' }}>Qty</th>
                      <th className="text-right py-3 px-2 text-sm font-bold" style={{ color: theme.gray[700], whiteSpace: 'nowrap' }}>Price</th>
                      <th className="text-right py-3 px-2 text-sm font-bold" style={{ color: theme.gray[700], whiteSpace: 'nowrap' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsToDisplay.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <tr className="border-b" style={{ borderColor: theme.gray[200] }}>
                          <td className="py-3 px-2">
                            <p className="font-semibold text-sm" style={{ color: theme.text }}>{item.menuItemName}</p>
                            {item.portionName && (
                              <p className="text-xs" style={{ color: theme.gray[500] }}>Portion: {item.portionName}</p>
                            )}
                            {item.toppings && item.toppings.length > 0 && (
                              <p className="text-xs" style={{ color: theme.gray[500] }}>
                                + {item.toppings.map(t => t.name).join(', ')}
                              </p>
                            )}
                            {item.comment && (
                              <p className="text-xs italic mt-1" style={{ color: theme.gray[500] }}>
                                Note: {item.comment}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-2 text-center text-sm" style={{ color: theme.text }}>
                            {item.quantity}
                          </td>
                          <td className="py-3 px-2 text-right text-sm" style={{ color: theme.text }}>
                            {CURRENCY.symbol}{item.price.toFixed(2)}
                          </td>
                          <td className="py-3 px-2 text-right text-sm font-semibold" style={{ color: theme.text }}>
                            {CURRENCY.symbol}{(item.price * item.quantity).toFixed(2)}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: theme.gray[500] }}><em>No items found in this order.</em></p>
            )}
          </section>

          {/* Totals Section with Tax Breakdown */}
          <div className="pt-6 border-t-2" style={{ borderColor: theme.gray[300] }}>
            <div className="space-y-2 max-w-sm ml-auto">
              {/* Subtotal */}
              <div className="flex justify-between text-sm">
                <span style={{ color: theme.gray[700] }}>Subtotal:</span>
                <span className="font-semibold" style={{ color: theme.text }}>
                  {CURRENCY.symbol}{(invoice.subtotal !== undefined && invoice.subtotal !== null ? invoice.subtotal : subtotal).toFixed(2)}
                </span>
              </div>

              {/* Discount — before taxes/service charge: they are computed on the
                  discounted base, so display order mirrors calculation order */}
              {invoice.discountAmount !== undefined && invoice.discountAmount !== null && invoice.discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.gray[700] }}>Discount:</span>
                  <span className="font-semibold text-green-600">-{CURRENCY.symbol}{invoice.discountAmount.toFixed(2)}</span>
                </div>
              )}

              {/* CGST */}
              {invoice.cgstAmount !== undefined && invoice.cgstAmount !== null && invoice.cgstAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.gray[700] }}>
                    CGST {invoice.cgstRate ? `(${invoice.cgstRate}%)` : ''}:
                  </span>
                  <span className="font-semibold" style={{ color: theme.text }}>{CURRENCY.symbol}{invoice.cgstAmount.toFixed(2)}</span>
                </div>
              )}

              {/* SGST */}
              {invoice.sgstAmount !== undefined && invoice.sgstAmount !== null && invoice.sgstAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.gray[700] }}>
                    SGST {invoice.sgstRate ? `(${invoice.sgstRate}%)` : ''}:
                  </span>
                  <span className="font-semibold" style={{ color: theme.text }}>{CURRENCY.symbol}{invoice.sgstAmount.toFixed(2)}</span>
                </div>
              )}

              {/* IGST */}
              {invoice.igstAmount !== undefined && invoice.igstAmount !== null && invoice.igstAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.gray[700] }}>
                    IGST {invoice.igstRate ? `(${invoice.igstRate}%)` : ''}:
                  </span>
                  <span className="font-semibold" style={{ color: theme.text }}>{CURRENCY.symbol}{invoice.igstAmount.toFixed(2)}</span>
                </div>
              )}

              {/* Service Charge */}
              {invoice.serviceChargeAmount !== undefined && invoice.serviceChargeAmount !== null && invoice.serviceChargeAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.gray[700] }}>
                    Service Charge {invoice.serviceChargeRate ? `(${invoice.serviceChargeRate}%)` : ''}:
                  </span>
                  <span className="font-semibold" style={{ color: theme.text }}>{CURRENCY.symbol}{invoice.serviceChargeAmount.toFixed(2)}</span>
                </div>
              )}

              {/* Total */}
              <div className="flex justify-between pt-3 border-t" style={{ borderColor: theme.gray[300] }}>
                <span className="text-lg font-bold" style={{ color: theme.gray[900] }}>Total Amount:</span>
                <span className="text-xl font-bold" style={{ color: theme.primary }}>
                  {CURRENCY.symbol}{invoice.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t text-center" style={{ borderColor: theme.gray[200] }}>
            <p className="text-sm font-semibold mb-1" style={{ color: theme.gray[700] }}>Thank you for your business!</p>
            <p className="text-xs" style={{ color: theme.gray[500] }}>
              This is a computer-generated invoice and does not require a signature.
            </p>
          </div>
        </div>

        {/* Print Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => window.print()}
            className="px-6 py-3 rounded-lg font-semibold transition-colors"
            style={{
              backgroundColor: theme.primary,
              color: 'var(--text-inverse)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Print Invoice
          </button>
        </div>
      </div>
    );
  };

  return (
    <CardLayoutTheme
      title="Invoice Detail"
      categories={[{ id: 1, name: 'Invoice Information' }]}
      renderItems={renderInvoiceContent}
      hideSidebar={true}
      sidebarTitle="Invoice Details"
    />
  );
};

export default InvoiceDetail;
